import __var_0__ from '@stdlib/vendor/events.js';
import __var_1__ from '@stdlib/vendor/debug.js';
import __var_2__ from '@stdlib/utils/define-property';
import __var_3__ from '@stdlib/utils/define-read-only-property';
import __var_4__ from '@stdlib/random/base/minstd';
import __var_5__ from '@stdlib/utils/copy';
import __var_6__ from './defaults.json';
import __var_7__ from './validate.js';
import __var_8__ from './props/width/set.js';
import __var_9__ from './props/width/get.js';
import __var_10__ from './props/height/set.js';
import __var_11__ from './props/height/get.js';
import __var_12__ from './props/id/set.js';
import __var_13__ from './props/id/get.js';
import __var_14__ from './props/auto-render/set.js';
import __var_15__ from './props/auto-render/get.js';
import __var_16__ from './methods/render.js';
/**

*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var EventEmitter = __var_0__.EventEmitter;
var logger = __var_1__;
var defineProperty = __var_2__;
var setReadOnly = __var_3__;
var minstd = __var_4__;
var copy = __var_5__;
var defaults = __var_6__;
var validate = __var_7__;
var setWidth = __var_8__;
var getWidth = __var_9__;
var setHeight = __var_10__;
var getHeight = __var_11__;
var setID = __var_12__;
var getID = __var_13__;
var setAutoRender = __var_14__;
var getAutoRender = __var_15__;
var render = __var_16__;


// VARIABLES //

var debug = logger( 'clippath:main' );


// MAIN //

/**
* Clipping path constructor.
*
* @constructor
* @param {Options} options - constructor options
* @param {PositiveNumber} [options.width=400] - width
* @param {PositiveNumber} [options.height=400] - height
* @param {string} [options.id] - clipping path id
* @param {boolean} [options.autoRender=true] - indicates whether to re-render on a change event
* @throws {TypeError} must provide valid options
* @returns {ClipPath} clipping path instance
*
* @example
* var clipPath = new ClipPath({
*     'width': 500,
*     'height': 500
* });
*/
function ClipPath( options ) {
	var self;
	var opts;
	var err;
	if ( !( this instanceof ClipPath ) ) {
		return new ClipPath( options );
	}
	self = this;
	opts = copy( defaults );
	err = validate( opts, options );
	if ( err ) {
		throw err;
	}
	debug( 'Creating an instance with the following configuration: %s.', JSON.stringify( opts ) );
	EventEmitter.call( this );

	defineProperty( this, '_width', {
		'configurable': false,
		'enumerable': false,
		'writable': true,
		'value': opts.width
	});
	defineProperty( this, '_height', {
		'configurable': false,
		'enumerable': false,
		'writable': true,
		'value': opts.height
	});
	defineProperty( this, '_id', {
		'configurable': false,
		'enumerable': false,
		'writable': true,
		'value': opts.id || minstd().toString() // TODO: uuid
	});
	defineProperty( this, '_autoRender', {
		'configurable': false,
		'enumerable': false,
		'writable': true,
		'value': opts.autoRender
	});

	this.on( 'change', onChange );
	this.on( '_render', onRender );

	return this;

	/**
	* Callback invoked upon receiving a change event.
	*
	* @private
	*/
	function onChange() {
		debug( 'Received a change event.' );
		if ( self._autoRender ) { // eslint-disable-line no-underscore-dangle
			self.render();
		}
	}

	/**
	* Re-emits a render event.
	*
	* @private
	*/
	function onRender() {
		var args;
		var i;
		debug( 'Received a render event. Re-emitting...' );
		args = new Array( arguments.length+1 );
		args[ 0 ] = 'render';
		for ( i = 0; i < arguments.length; i++ ) {
			args[ i+1 ] = arguments[ i ];
		}
		self.emit.apply( self, args );
	}
}

/*
* Create a prototype which inherits from the parent prototype.
*/
ClipPath.prototype = Object.create( EventEmitter.prototype );

/*
* Set the constructor.
*/
ClipPath.prototype.constructor = ClipPath;

/**
* Width.
*
* @name width
* @memberof ClipPath.prototype
* @type {PositiveNumber}
* @throws {TypeError} must be a positive number
* @default 400
*
* @example
* var clipPath = new ClipPath({
*     'width': 500
* });
*
* var width = clipPath.width;
* // returns 500
*/
defineProperty( ClipPath.prototype, 'width', {
	'configurable': false,
	'enumerable': true,
	'set': setWidth,
	'get': getWidth
});

/**
* Height.
*
* @name height
* @memberof ClipPath.prototype
* @type {PositiveNumber}
* @throws {TypeError} must be a positive number
* @default 400
*
* @example
* var clipPath = new ClipPath({
*     'height': 500
* });
*
* var height = clipPath.height;
* // returns 500
*/
defineProperty( ClipPath.prototype, 'height', {
	'configurable': false,
	'enumerable': true,
	'set': setHeight,
	'get': getHeight
});

/**
* Clipping path id.
*
* @name id
* @memberof ClipPath.prototype
* @type {string}
* @throws {TypeError} must be a primitive string
*
* @example
* var clipPath = new ClipPath({
*     'id': '1234'
* });
*
* var id = clipPath.id;
* // returns '1234'
*/
defineProperty( ClipPath.prototype, 'id', {
	'configurable': false,
	'enumerable': true,
	'set': setID,
	'get': getID
});

/**
* Rendering mode. If `true`, an instance re-renders on each change event.
*
* @name autoRender
* @memberof ClipPath.prototype
* @type {boolean}
* @throws {TypeError} must be a boolean primitive
* @default false
*
* @example
* var clipPath = new ClipPath({
*     'autoRender': true
* });
*
* var mode = clipPath.autoRender;
* // returns true
*/
defineProperty( ClipPath.prototype, 'autoRender', {
	'configurable': false,
	'enumerable': true,
	'set': setAutoRender,
	'get': getAutoRender
});

/**
* Renders a virtual DOM tree.
*
* @name render
* @memberof ClipPath.prototype
* @type {Function}
* @returns {VTree} virtual tree
*
* @example
* var clipPath = new ClipPath();
*
* var out = clipPath.render();
*/
setReadOnly( ClipPath.prototype, 'render', render );


// EXPORTS //
export default ClipPath;
